import React from 'react';
import { ShieldAlert, Phone, ExternalLink } from 'lucide-react';
import AcceptancePolicyBlocks from './AcceptancePolicyBlocks';
import { DISPOSAL_RESOURCES } from '@/data/site';

/**
 * "Acceptance Policy" section.
 * Used on the homepage (after FAQ), the quote/contact page, and the /junk-removal service page.
 *
 * Variants:
 *   - "default": padded full-width section with surface background
 *   - "inset":   tighter card layout for placement inside another container
 */
export default function CannotTake({ variant = 'default' }) {
  const inset = variant === 'inset';

  return (
    <section
      className={inset ? '' : 'section-pad bg-ohana-surface'}
      data-testid="cannot-take-section"
    >
      <div className={inset ? '' : 'container-ohana'}>
        <div className="max-w-3xl">
          <div className="flex items-center gap-3">
            <span className="grid h-12 w-12 place-items-center rounded-xl bg-ohana-primary text-ohana-bg">
              <ShieldAlert className="h-6 w-6" />
            </span>
            <p className="overline">Acceptance Policy</p>
          </div>
          <h2
            className={`h-display mt-5 leading-tight ${
              inset ? 'text-3xl md:text-4xl' : 'text-4xl md:text-5xl'
            }`}
            data-testid="cannot-take-title"
          >
            What we take, what we don't.
          </h2>
          <p className="mt-5 text-ohana-text-muted leading-relaxed">
            For the safety of our crew and compliance with Bartholomew County and Indiana solid waste regulations, here is exactly what Ohana Property Services accepts and what we do not. If you have items outside our scope we will help you find the right disposal resource.
          </p>
        </div>

        <div className="mt-10">
          <AcceptancePolicyBlocks />
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          {DISPOSAL_RESOURCES.map((r) => (
            <div
              key={r.label}
              className="rounded-2xl border border-ohana-border bg-white p-6"
              data-testid={`disposal-resource-${r.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            >
              <p className="text-sm font-bold text-ohana-primary">{r.label}</p>
              <p className="text-xs text-ohana-text-muted mt-1">{r.detail}</p>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                {r.href && (
                  <a
                    href={r.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-ohana-primary font-semibold underline decoration-ohana-accent decoration-2 underline-offset-4 hover:text-ohana-accent-dark"
                    data-testid={`disposal-link-${r.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  >
                    {r.linkLabel || r.href}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
                {r.phone && (
                  <span
                    className="inline-flex items-center gap-1.5 text-ohana-text-muted"
                    data-testid={`disposal-phone-${r.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  >
                    <Phone className="h-3.5 w-3.5" />
                    {r.phone}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
